import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';

const colorPalette = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#14B8A6', '#A855F7',
  '#F43F5E', '#22C55E', '#EAB308', '#0EA5E9', '#6366F1', '#DC2626'
];

const SoilingLossChart = ({ startDate, endDate, assetIds = [] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [seriesKeys, setSeriesKeys] = useState([]);
  const [rows, setRows] = useState([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await apiService.getForEnergyLoss({
        start_date: `${startDate} 00:00:00`,
        end_date: `${endDate} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: assetIds.length ? assetIds : [API_CONFIG.PLANT_ID]
      });
      const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
      if (!Array.isArray(arr) || arr.length === 0) {
        setSeriesKeys([]);
        setRows([]);
        return;
      }
      const keysSet = new Set();
      arr.forEach(d => {
        const map = d?.readings?.[0]?.EYA?.soiling_loss_wms_level || {};
        Object.keys(map).forEach(k => keysSet.add(k));
      });
      const keys = Array.from(keysSet);
      const dataRows = arr.map(d => {
        const date = new Date(d.timestamp);
        const label = date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
        const map = d?.readings?.[0]?.EYA?.soiling_loss_wms_level || {};
        const row = { date: label };
        keys.forEach(k => {
          const v = map[k];
          row[k] = typeof v === 'number' ? Math.abs(Number(v)) : null;
        });
        return row;
      });
      setSeriesKeys(keys);
      setRows(dataRows);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const bars = useMemo(() => seriesKeys.map((key, idx) => (
    <Bar key={key} dataKey={key} name={key.replace(/_wms/g, '').replace(/_/g, ' ')} fill={colorPalette[idx % colorPalette.length]} />
  )), [seriesKeys]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Soiling Loss (%)</CardTitle>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-80 w-full" />
        ) : rows.length === 0 || seriesKeys.length === 0 ? (
          <div className="text-center text-gray-500">No Soiling Loss data</div>
        ) : (
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 60, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                <YAxis label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name]} />
                <Legend />
                {bars}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SoilingLossChart;


